import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Loader2, Sparkles, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const DISMISS_KEY = 'pullover.invite_nudge_dismissed';
const SOLD_THRESHOLD = 3;

/**
 * Solo-only nudge: after the user has logged 3+ sold deals, surface an
 * "Invite a teammate" card. Sending the invite flips the workspace from solo
 * to multi-seat (is_solo = false) and the existing PilotUserList takes over.
 */
export function InviteTeammateCard() {
  const { mode, isSolo, dealershipId, userId, refreshProfile } = useAuth();
  const [soldCount, setSoldCount] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
  }, []);

  useEffect(() => {
    if (mode !== 'real' || !isSolo || !dealershipId) return;
    let active = true;
    (async () => {
      const { count } = await supabase
        .from('deals')
        .select('id', { count: 'exact', head: true })
        .eq('dealership_id', dealershipId)
        .eq('stage', 'sold');
      if (active) setSoldCount(count ?? 0);
    })();
    return () => { active = false; };
  }, [mode, isSolo, dealershipId]);

  if (mode !== 'real' || !isSolo) return null;
  if (dismissed) return null;
  if (soldCount === null || soldCount < SOLD_THRESHOLD) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleInvite = async () => {
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!dealershipId || !userId) return;
    setSending(true);
    try {
      // 1. Convert solo workspace to multi-seat first (so role/policy intent is clear).
      const { error: updErr } = await supabase
        .from('dealerships')
        .update({ is_solo: false })
        .eq('id', dealershipId);
      if (updErr) throw updErr;

      // 2. Send the invite via existing edge function.
      const { data, error } = await supabase.functions.invoke('invite-rep', {
        body: { email: cleaned, full_name: name.trim(), role: 'rep' },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      toast.success(`Invite sent to ${cleaned}. Your workspace is now multi-seat.`);
      setOpen(false);
      setEmail('');
      setName('');
      await refreshProfile();
    } catch (e: any) {
      // Best effort rollback if invite failed but we already flipped is_solo
      await supabase.from('dealerships').update({ is_solo: true }).eq('id', dealershipId);
      toast.error(e?.message ?? 'Could not send invite');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> You're on a roll — invite a teammate?
          </CardTitle>
          <CardDescription>
            You've closed {soldCount} deals on PullOver. Bring a teammate on so you can compare notes, share leads, and run a real leaderboard.
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={handleDismiss} aria-label="Dismiss">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {!open ? (
          <Button onClick={() => setOpen(true)}>
            <UserPlus className="h-4 w-4" /> Invite a teammate
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="solo-invite-name">Their name (optional)</Label>
              <Input
                id="solo-invite-name"
                placeholder="Alex"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="solo-invite-email">Their email</Label>
              <Input
                id="solo-invite-email"
                type="email"
                placeholder="alex@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleInvite} disabled={sending}>
                {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : <>Send invite</>}
              </Button>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}>
                Cancel
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Heads up: this converts your solo workspace into a multi-seat dealership. You'll become the manager.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
